import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type Board, type UploadUrlResponse } from '@air/api/types';
import { isAirror, MAX_ANCESTOR_COUNT_EXCEEDED } from '@air/errors';
import {
  setSmallUploadCancellablesAction,
  setSmallUploadProgressAction,
  setUploadCompletedAction,
  setUploadFailedAction,
  setUploadPreparingAction,
  setUploadUploadingAction,
  setUploadUrlInfoAction,
  smallUploadByIdSelector,
  type Upload,
  UploadStatus,
  useUploaderStore,
} from '@air/redux-uploader';
import { uploadFile } from '@air/upload';

import { type UploadFileBaseParams } from '../types';
import { isAbortError } from '../utilities';

export type UploadSmallParams = {
  uploadId: Upload['id'];
  parentBoardId?: Board['id'];
  onUploadStarted: UploadFileBaseParams['onUploadStarted'];
  onUploadFinished: (params: { uploadId: Upload['id'] }) => Promise<void> | void;
  getUploadUrls: (upload: Upload) => Promise<UploadUrlResponse[0] | undefined>;
  onError: (params: { upload: Upload; error: unknown }) => void;
};

export const useUploadSmall = () => {
  const store = useUploaderStore();
  const dispatch = useDispatch();

  const uploadSmall = useCallback(
    async ({ uploadId, getUploadUrls, onUploadStarted, onUploadFinished, onError }: UploadSmallParams) => {
      const upload = smallUploadByIdSelector(store.getState(), uploadId);

      if (upload && upload.status === UploadStatus.queued) {
        dispatch(setUploadPreparingAction({ uploadId: upload.id }));
        try {
          let uploadUrlInfo = upload.s3Info.uploadUrlInfo;
          if (!uploadUrlInfo) {
            uploadUrlInfo = await getUploadUrls(upload);
            if (uploadUrlInfo) {
              dispatch(setUploadUrlInfoAction({ uploadId: upload.id, uploadUrlInfo }));
            }
          }

          dispatch(setUploadUploadingAction({ uploadId: upload.id }));
          if (uploadUrlInfo) {
            const onProgress: UploadFileBaseParams['onProgress'] = ({ progress, abort }) => {
              if (progress) {
                dispatch(setSmallUploadProgressAction({ abort, progress, uploadId: upload.id }));
              }
            };

            const onUploadStart = (xhr: XMLHttpRequest) => {
              dispatch(setSmallUploadCancellablesAction({ uploadId: upload.id, uploadUrlInfo, xhr }));
              onUploadStarted({ uploadId: upload.id });
            };

            const { url, mime } = uploadUrlInfo;

            await uploadFile({ contentType: mime, file: upload.file, onProgress, onStart: onUploadStart, url });

            await onUploadFinished({ uploadId: upload.id });
            dispatch(setUploadCompletedAction({ uploadId: upload.id }));
          } else {
            // TODO: handle error
          }
        } catch (error) {
          if (!isAbortError(error)) {
            dispatch(
              setUploadFailedAction({
                errorMessage:
                  isAirror(error) && error.type === MAX_ANCESTOR_COUNT_EXCEEDED.type
                    ? 'Max ancestor count exceeded'
                    : 'Failed',
                uploadId: upload.id,
              }),
            );
          }
          onError({ error, upload });
        }
      }
    },
    [dispatch, store],
  );

  return {
    uploadSmall,
  };
};
